import React, { useCallback, useEffect, useRef } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import {
  StarRounded,
  StarBorderRounded,
  ExpandMore,
  ChevronLeft,
  KeyboardArrowDown,
  CloseRounded,
  LockOutlined,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useShellTranslation } from './useShellTranslation';
import { shellTokens, navIconSize } from './shellTokens';
import { NavGroup, NavLeaf } from './navConfig';
import { NavModel } from './useNavModel';
import { useShell } from './ShellContext';
import { useOverlayFocus } from './useOverlayFocus';
import { TrialFeatureBadge } from '../trial/TrialFeatureBadge';

const { color, size, radius, shadow, motion, z } = shellTokens;

/** Flyout geometry, used to keep a rail flyout inside the viewport. */
const FLYOUT_ROW_HEIGHT = 35;
const FLYOUT_HEADER_HEIGHT = 34;
const FLYOUT_VIEWPORT_MARGIN = 12;

interface SidebarProps {
  nav: NavModel;
  /** Mobile always renders labels; the rail is a desktop-only affordance. */
  isMobile: boolean;
  identity: { title: string; subtitle: string; initials: string };
  profile: { name: string; role: string; initial: string };
}

/** Sidebar wordmark, or the standalone glyph once collapsed to the rail. */
const BrandRow: React.FC<{
  rail: boolean;
  isMobile: boolean;
  onClose: () => void;
  closeLabel: string;
}> = ({ rail, isMobile, onClose, closeLabel }) => (
  <Box
    sx={{
      height: size.header,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: rail ? 'center' : 'space-between',
      px: rail ? 0 : '16px',
      borderBottom: `1px solid ${color.lineSubtle}`,
    }}
  >
    {rail ? (
      <Box
        component="svg"
        width={24}
        height={26}
        viewBox="15 3 70 94"
        fill={color.brandGlyph}
        sx={{ flex: '0 0 auto' }}
        aria-hidden
      >
        <rect x="15" y="3" width="40" height="10" />
        <rect x="45" y="15" width="40" height="10" />
        <rect x="20" y="27" width="40" height="10" />
        <rect x="40" y="39" width="40" height="10" />
        <rect x="27" y="51" width="40" height="10" />
        <rect x="33" y="63" width="40" height="10" />
        <rect x="30" y="75" width="40" height="10" />
        <rect x="30" y="87" width="40" height="10" />
      </Box>
    ) : (
      <Box
        component="img"
        src="/logo-black.svg"
        alt="Motzu Labs"
        sx={{ height: 20, width: 'auto' }}
      />
    )}
    {isMobile && !rail && (
      <ButtonBase
        onClick={onClose}
        aria-label={closeLabel}
        sx={{ width: 34, height: 34, borderRadius: radius.control, color: color.muted }}
      >
        <CloseRounded sx={{ fontSize: 20 }} />
      </ButtonBase>
    )}
  </Box>
);

/**
 * Answers "whose data am I looking at". Deliberately inert — cross-account
 * viewing goes through impersonation, which is audited and read-only.
 */
const AccountIdentity: React.FC<SidebarProps['identity']> = ({ title, subtitle, initials }) => (
  <Box sx={{ p: '12px 12px 8px', flexShrink: 0 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.control,
        p: '8px 10px',
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: radius.menuRow,
          background: color.ink,
          color: color.white,
          fontSize: '11.5px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: color.ink }}>
          {title}
        </Typography>
        <Typography noWrap sx={{ fontSize: '11.5px', color: color.muted }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      p: '6px 10px 6px',
      mb: '2px',
      borderBottom: `1px solid ${color.lineSubtle}`,
    }}
  >
    <StarRounded sx={{ fontSize: 11, color: color.pin }} />
    <Typography
      sx={{
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        color: color.faint,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const NavBadge: React.FC<{ value: number; active: boolean }> = ({ value, active }) => (
  <Box
    sx={{
      flexShrink: 0,
      p: '1px 6px',
      borderRadius: radius.pill,
      fontSize: '11px',
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      background: active ? color.accentBadge : color.lineSubtle,
      color: active ? color.accentDeep : color.muted,
    }}
  >
    {value}
  </Box>
);

interface NavRowProps {
  leaf: NavLeaf;
  label: string;
  active: boolean;
  badge: number;
  child?: boolean;
  showPin?: boolean;
  rail?: boolean;
  /** Rendered inside the rail flyout, which is a menu. */
  menuItem?: boolean;
  onNavigate: (leaf: NavLeaf) => void;
  /** Set when the row is visible but the plan does not include it. */
  lockedPlan?: string | null;
}

const NavRow: React.FC<NavRowProps> = ({
  leaf,
  label,
  active,
  badge,
  child = false,
  showPin = true,
  rail = false,
  menuItem = false,
  onNavigate,
  lockedPlan = null,
}) => {
  const { isPinned, togglePin } = useShell();
  const { t } = useShellTranslation();
  const pinned = isPinned(leaf.path);

  const showPinControl = showPin && !rail;
  /**
   * Only root rows and group headers carry an icon. Inside an expanded group
   * the indent rail already encodes the hierarchy, and a second icon column
   * competes with the group's own icon for the same meaning — so child rows and
   * flyout rows are label, badge and pin alone.
   */
  const showIcon = !child && !menuItem;
  const locked = lockedPlan != null;
  const lockedNotice = locked
    ? lockedPlan
      ? t('planLock.required', { plan: lockedPlan })
      : t('planLock.requiredUnknown')
    : '';

  if (locked) {
    const lockedRow = (
      <Box
        aria-disabled="true"
        aria-label={rail ? `${label}. ${lockedNotice}` : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: rail ? 0 : '8px',
          opacity: 0.72,
          borderRadius: child ? radius.menuRow : radius.navRow,
          p: rail ? '8px 0' : '8px 10px',
          justifyContent: rail ? 'center' : 'flex-start',
        }}
      >
        {showIcon && (
          <Box className="shell-nav-icon" sx={{ display: 'flex', color: color.faint, '& svg': { fontSize: navIconSize } }}>
            {leaf.icon}
          </Box>
        )}
        {rail ? (
          <LockOutlined sx={{ fontSize: 16, color: color.faint }} />
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 500, color: color.muted }}>
              {label}
            </Typography>
            <Typography
              component={RouterLink}
              to="/app/billing"
              noWrap
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11.5px',
                color: color.muted,
                textDecoration: 'none',
              }}
            >
              <LockOutlined sx={{ fontSize: 12 }} />
              {lockedNotice}
            </Typography>
          </Box>
        )}
      </Box>
    );
    return rail ? (
      <Tooltip title={`${label}. ${lockedNotice}`} placement="right">
        {lockedRow}
      </Tooltip>
    ) : (
      lockedRow
    );
  }

  const row = (
    // The pin is a sibling of the navigation button, never a child of it:
    // nesting one interactive control inside another is invalid and leaves
    // screen readers describing a button inside a button.
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        '&:hover .shell-pin': { opacity: 1 },
      }}
    >
    <ButtonBase
      onClick={() => onNavigate(leaf)}
      role={menuItem ? 'menuitem' : undefined}
      tabIndex={menuItem ? -1 : undefined}
      aria-current={active ? 'page' : undefined}
      // Rail mode hides the visible label and the tooltip wraps this Box, not
      // the button — accessible names do not propagate from an ancestor.
      aria-label={rail ? label : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: rail ? 'center' : 'flex-start',
        gap: rail ? 0 : child || menuItem ? '9px' : '10px',
        borderRadius: child ? radius.menuRow : radius.navRow,
        p: rail ? '8px 0' : '8px 10px',
        pr: showPinControl ? '32px' : undefined,
        textAlign: 'left',
        background: active ? color.accentTint : 'transparent',
        '&:hover': { background: active ? color.accentTint : color.lineSubtle },
        '&::before': active
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '6px',
              bottom: '6px',
              width: child ? '2px' : '3px',
              background: color.accent,
              borderRadius: '0 3px 3px 0',
            }
          : undefined,
        '& .shell-nav-icon': {
          flex: '0 0 auto',
          display: 'flex',
          color: active ? color.accent : color.faint,
          '& svg': { fontSize: navIconSize },
        },
      }}
    >
      {showIcon && <Box className="shell-nav-icon">{leaf.icon}</Box>}
      {!rail && (
        <>
          <Typography
            noWrap
            sx={{
              flex: leaf.planFeature ? '0 1 auto' : 1,
              minWidth: 0,
              fontSize: '13.5px',
              fontWeight: active ? 600 : 500,
              color: active ? color.accentDeep : color.body,
            }}
          >
            {label}
          </Typography>
          {leaf.planFeature && (
            // The label wins: the trial tier badge takes only the room left
            // beside it. One clipped line that wraps after a zero-width spacer
            // (a lone flex item never wraps), so a badge that does not fit whole
            // drops out of sight instead of squeezing the label ("Rapport des
            // remises" leaves no room for "Intelligence"). The spacer is the
            // line's full height, or the wrapped line would start at the top.
            <Box
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                // A 5px gap to the label rather than the row's 9px.
                ml: '-4px',
                height: 16,
                overflow: 'hidden',
                display: 'flex',
                flexWrap: 'wrap',
                '&::before': { content: '""', flex: '1 0 0', height: 16 },
              }}
            >
              <TrialFeatureBadge feature={leaf.planFeature} compact />
            </Box>
          )}
          {badge > 0 && <NavBadge value={badge} active={active} />}
        </>
      )}
    </ButtonBase>
    {showPinControl && (
      <ButtonBase
        className="shell-pin"
        aria-label={pinned ? t('shell.pins.remove') : t('shell.pins.add')}
        aria-pressed={pinned}
        onClick={() => togglePin(leaf.path)}
        sx={{
          position: 'absolute',
          right: '8px',
          width: 20,
          height: 20,
          borderRadius: '4px',
          flexShrink: 0,
          opacity: pinned ? 1 : 0,
          transition: 'opacity .15s ease',
          color: pinned ? color.pin : color.pinEmpty,
          '& svg': { fontSize: 15 },
          // Revealing on hover alone leaves an invisible tap target on touch
          // devices, and hides the control from keyboard users entirely.
          // Keyed on :focus rather than :focus-visible only, so the control is
          // never focused-but-invisible however focus arrived.
          '&:focus': { opacity: 1 },
          '&:focus-visible': { opacity: 1, outline: `2px solid ${color.accent}` },
          '@media (hover: none)': { opacity: 1 },
        }}
      >
        {pinned ? <StarRounded /> : <StarBorderRounded />}
      </ButtonBase>
    )}
    </Box>
  );

  return rail ? (
    <Tooltip title={label} placement="right">
      {row}
    </Tooltip>
  ) : (
    row
  );
};

interface GroupBlockProps {
  group: NavGroup;
  nav: NavModel;
  rail: boolean;
  onNavigate: (leaf: NavLeaf) => void;
}

const GroupBlock: React.FC<GroupBlockProps> = ({ group, nav, rail, onNavigate }) => {
  const { t } = useShellTranslation();
  const { openGroups, toggleGroup, setOverlay, overlay } = useShell();
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const holdsActive = group.items.some(nav.isActive);
  const open = Boolean(openGroups[group.id]) || holdsActive;
  const label = t(`shell.group.${group.labelKey}`);
  const flyoutOpen = overlay?.kind === 'flyout' && overlay.groupId === group.id;

  const handleClick = () => {
    if (rail) {
      const rect = rowRef.current?.getBoundingClientRect();
      // The sidebar clips, so the flyout is viewport-anchored off the row
      // itself. Reserving a flat gap below the trigger is not enough — a
      // seven-item group opened near the bottom of the rail would run past the
      // viewport with its lower links unreachable — so estimate the panel and
      // lift it until it fits. RailFlyout caps and scrolls the remainder.
      const estimatedHeight = FLYOUT_HEADER_HEIGHT + group.items.length * FLYOUT_ROW_HEIGHT;
      const maxTop = window.innerHeight - FLYOUT_VIEWPORT_MARGIN - estimatedHeight;
      const top = rect
        ? Math.max(FLYOUT_VIEWPORT_MARGIN, Math.min(rect.top, maxTop))
        : FLYOUT_VIEWPORT_MARGIN;
      setOverlay(flyoutOpen ? null : { kind: 'flyout', groupId: group.id, top });
      return;
    }
    toggleGroup(group.id);
  };

  const header = (
    <ButtonBase
      ref={rowRef}
      onClick={handleClick}
      aria-expanded={rail ? flyoutOpen : open}
      aria-label={rail ? label : undefined}
      aria-haspopup={rail ? 'menu' : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: rail ? 'center' : 'flex-start',
        gap: rail ? 0 : '10px',
        borderRadius: radius.navRow,
        p: rail ? '8px 0' : '8px 10px',
        textAlign: 'left',
        // A collapsed group holding the active page still has to read as "you are in here".
        background: holdsActive && !open ? color.surface : 'transparent',
        '&:hover': { background: color.lineSubtle },
        '& .shell-nav-icon': {
          flex: '0 0 auto',
          display: 'flex',
          color: holdsActive ? color.accent : color.faint,
          '& svg': { fontSize: navIconSize },
        },
      }}
    >
      <Box className="shell-nav-icon">{group.icon}</Box>
      {!rail && (
        <>
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '13.5px',
              fontWeight: holdsActive ? 600 : 500,
              color: holdsActive ? color.ink : color.body,
            }}
          >
            {label}
          </Typography>
          <ExpandMore
            sx={{
              fontSize: 12,
              color: color.faint,
              transition: motion.chevron,
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </>
      )}
    </ButtonBase>
  );

  return (
    <Box>
      {rail ? (
        <Tooltip title={label} placement="right">
          {header}
        </Tooltip>
      ) : (
        header
      )}
      {!rail && open && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            pl: '13px',
            ml: '13px',
            mt: '2px',
            borderLeft: `1px solid ${color.lineSubtle}`,
          }}
        >
          {group.items.map((leaf) => (
            <NavRow
              key={leaf.id}
              leaf={leaf}
              label={t(`shell.item.${leaf.labelKey}`)}
              active={nav.isActive(leaf)}
              badge={nav.badgeFor(leaf)}
              child
              lockedPlan={nav.lockName(leaf)}
              onNavigate={onNavigate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/** Rail-mode group menu. Fixed-position because the sidebar clips at 68px. */
export const RailFlyout: React.FC<{ nav: NavModel }> = ({ nav }) => {
  const { t } = useShellTranslation();
  const navigate = useNavigate();
  const { overlay, closeOverlay } = useShell();
  const { containerRef, onKeyDown } = useOverlayFocus<HTMLDivElement>(
    overlay?.kind === 'flyout',
    { menu: true },
  );
  if (overlay?.kind !== 'flyout') return null;
  const group = nav.groups.find((candidate) => candidate.id === overlay.groupId);
  if (!group) return null;

  return (
    <>
      <Box
        onClick={closeOverlay}
        sx={{ position: 'fixed', inset: 0, zIndex: z.flyout - 1 }}
        aria-hidden
      />
      <Box
        ref={containerRef}
        role="menu"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        aria-label={t(`shell.group.${group.labelKey}`)}
        sx={{
          position: 'fixed',
          left: 76,
          top: overlay.top,
          width: 230,
          // Belt and braces: the anchor already lifts the panel to fit, but a
          // group taller than the viewport still has to scroll rather than
          // spill past the bottom edge.
          maxHeight: `calc(100vh - ${overlay.top + FLYOUT_VIEWPORT_MARGIN}px)`,
          overflowY: 'auto',
          zIndex: z.flyout,
          background: color.white,
          border: `1px solid ${color.line}`,
          borderRadius: radius.menu,
          boxShadow: shadow.flyout,
          p: '6px',
        }}
      >
        <Typography
          sx={{
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: color.faint,
            p: '6px 10px 4px',
          }}
        >
          {t(`shell.group.${group.labelKey}`)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {group.items.map((leaf) => (
            <NavRow
              key={leaf.id}
              leaf={leaf}
              label={t(`shell.item.${leaf.labelKey}`)}
              active={nav.isActive(leaf)}
              badge={nav.badgeFor(leaf)}
              showPin={false}
              lockedPlan={nav.lockName(leaf)}
              menuItem
              onNavigate={(target) => {
                closeOverlay();
                navigate(target.path);
              }}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ nav, isMobile, identity, profile }) => {
  const { t } = useShellTranslation();
  const navigate = useNavigate();
  const {
    rail: railState,
    toggleRail,
    pinnedPaths,
    openGroup,
    setOverlay,
    setMobileOpen,
  } = useShell();
  const rail = railState && !isMobile;
  const profileRef = useRef<HTMLButtonElement | null>(null);

  const handleNavigate = useCallback(
    (leaf: NavLeaf) => {
      navigate(leaf.path);
      if (isMobile) setMobileOpen(false);
    },
    [navigate, isMobile, setMobileOpen],
  );

  // Landing on a page inside a collapsed group should reveal where you are.
  // Keyed on the id rather than the group object: the object identity is not
  // guaranteed stable across renders, and re-running this on every render made
  // the accordion snap back to the active group the instant you opened another.
  const activeGroupId = nav.activeGroup?.id ?? null;
  useEffect(() => {
    if (activeGroupId) openGroup(activeGroupId);
  }, [activeGroupId, openGroup]);

  const pinnedLeaves = pinnedPaths
    .map((path) => nav.entries.find((entry) => entry.leaf.path === path)?.leaf)
    .filter((leaf): leaf is NavLeaf => Boolean(leaf));

  const openProfileMenu = () => {
    const rect = profileRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Measured every open: the sidebar clips, so the menu is viewport-anchored.
    const at = { left: rect.left, bottom: window.innerHeight - rect.top + 8 };
    // On mobile the drawer is the active modal. Leaving it open would put the
    // menu behind the drawer's focus and aria-hidden handling, and any
    // navigation from it would land under the still-open drawer. The anchor is
    // already captured, so the menu stays where the row was.
    if (isMobile) setMobileOpen(false);
    setOverlay({ kind: 'profile', at });
  };

  return (
    <Box
      component="aside"
      sx={{
        // Inside the mobile drawer the paper owns the width; pinning 264px here
        // would leave a dead strip between the nav and the drawer edge.
        width: isMobile ? '100%' : rail ? size.rail : size.sidebar,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: color.white,
        borderRight: `1px solid ${color.line}`,
        transition: motion.sidebar,
      }}
    >
      <BrandRow
        rail={rail}
        isMobile={isMobile}
        onClose={() => setMobileOpen(false)}
        closeLabel={t('shell.nav.closeMenu')}
      />

      {!rail && <AccountIdentity {...identity} />}

      <Box
        component="nav"
        aria-label={t('shell.nav.primary')}
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          p: rail ? '4px 8px 16px' : '4px 12px 16px',
        }}
      >
        {!rail && pinnedLeaves.length > 0 && (
          <>
            <SectionHeader label={t('shell.pins.section')} />
            {pinnedLeaves.map((leaf) => (
              <NavRow
                key={`pinned-${leaf.id}`}
                leaf={leaf}
                label={t(`shell.item.${leaf.labelKey}`)}
                active={nav.isActive(leaf)}
                badge={nav.badgeFor(leaf)}
                lockedPlan={nav.lockName(leaf)}
                onNavigate={handleNavigate}
              />
            ))}
            <Box sx={{ height: '6px' }} />
          </>
        )}

        {nav.roots.map((leaf) => (
          <NavRow
            key={leaf.id}
            leaf={leaf}
            label={t(`shell.item.${leaf.labelKey}`)}
            active={nav.isActive(leaf)}
            badge={nav.badgeFor(leaf)}
            rail={rail}
            showPin={!rail}
            lockedPlan={nav.lockName(leaf)}
            onNavigate={handleNavigate}
          />
        ))}

        {nav.groups.map((group) => (
          <GroupBlock
            key={group.id}
            group={group}
            nav={nav}
            rail={rail}
            onNavigate={handleNavigate}
          />
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${color.lineSubtle}`,
          p: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          pb: isMobile ? 'calc(8px + env(safe-area-inset-bottom))' : '8px',
        }}
      >
        {!rail && nav.role === 'vendor' && (
          <Box sx={{ p: '8px 8px 4px', borderBottom: `1px solid ${color.lineSubtle}` }}>
            <Typography
              sx={{
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: color.faint,
              }}
            >
              {t('shell.footer.brandMetrics.title')}
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: color.muted, mt: '2px' }}>
              {t('shell.footer.brandMetrics.body')}
            </Typography>
          </Box>
        )}
        <ButtonBase
          ref={profileRef}
          onClick={openProfileMenu}
          aria-haspopup="menu"
          aria-label={t('shell.profile.open')}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: rail ? 'center' : 'flex-start',
            gap: rail ? 0 : '10px',
            p: rail ? '6px 0' : '6px 8px',
            border: rail ? 'none' : `1px solid ${color.line}`,
            borderRadius: radius.control,
            '&:hover': { background: color.lineSubtle },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: '50%',
              background: color.ink,
              color: color.white,
              fontSize: '12px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {profile.initial}
          </Box>
          {!rail && (
            <>
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: color.ink }}>
                  {profile.name}
                </Typography>
                <Typography noWrap sx={{ fontSize: '11.5px', color: color.muted }}>
                  {profile.role}
                </Typography>
              </Box>
              <KeyboardArrowDown sx={{ fontSize: 12, color: color.faint }} />
            </>
          )}
        </ButtonBase>

        {!isMobile && (
          <ButtonBase
            onClick={toggleRail}
            aria-label={rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: rail ? 'center' : 'flex-start',
              gap: rail ? 0 : '10px',
              p: rail ? '6px 0' : '6px 8px',
              borderRadius: radius.menuRow,
              color: color.muted,
              '&:hover': { background: color.lineSubtle, color: color.ink },
            }}
          >
            <ChevronLeft
              sx={{
                fontSize: 16,
                transition: motion.chevron,
                transform: rail ? 'rotate(180deg)' : 'none',
              }}
            />
            {!rail && (
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'inherit' }}>
                {t('shell.sidebar.collapse')}
              </Typography>
            )}
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};
